import type { InvoiceAttachment } from '@clockwise/clients/invoices';
import { ApiError, INVOICE_ATTACHMENT_ACCEPT, MAX_INVOICE_ATTACHMENTS } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Paperclip, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { FileDropZone } from '@/components/custom/file-drop-zone';
import { screenFiles } from '@/components/custom/invoice-attachment-rules';
import { Button } from '@/components/ui/button';
import { invoicesApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { apiErrorText } from '@/lib/errors';
import { formatBytes } from '@/lib/format-bytes';
import { queryKeys } from '@/lib/query-keys';

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

/** The message to show for a failed upload: the server's field message when
 *  it gave one ("sow.pdf is too large…"), else its general message. */
const describeError = (err: unknown) => apiErrorText(err, 'the upload failed');

/**
 * The invoice's attachments: list, download, remove, and a drop zone to add.
 *
 * Deliberately NOT part of the invoice form. Each file is its own request to
 * its own endpoint, so attaching one never dirties the form, never needs the
 * Save button, and — because it does not touch the invoice row — never
 * returns an approved invoice to draft.
 *
 * `editable` is the invoice's own rule (a draft the caller may edit). When it
 * is false the list stays readable and downloadable but closed. `lockedBy`
 * says why, for the note that explains it — and is null when the invoice is
 * not locked at all, only closed to this viewer (a draft they may not edit),
 * which needs no note: there is nothing for them to do about it.
 */
export function InvoiceAttachments({
  invoiceId,
  editable,
  lockedBy = 'sent',
}: {
  invoiceId: string;
  editable: boolean;
  lockedBy?: 'sent' | 'approved' | null;
}) {
  const queryClient = useQueryClient();
  const key = queryKeys.invoices.attachments(invoiceId);
  const list = useQuery({ queryKey: key, queryFn: () => invoicesApi.listAttachments(invoiceId) });
  const attachments = list.data?.data ?? [];

  const [problems, setProblems] = useState<string[]>([]);
  const [uploading, setUploading] = useState(0);
  const [toRemove, setToRemove] = useState<InvoiceAttachment | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) =>
      // A fresh key per file: a retry of THIS upload replays, a second file
      // with the same name is a second attachment.
      invoicesApi.uploadAttachment(invoiceId, file, { idempotencyKey: crypto.randomUUID() }),
  });

  const handleFiles = async (files: File[]) => {
    const screened = screenFiles(files, attachments.length);
    const failures = [...screened.rejected];
    let added = 0;
    setUploading(screened.accepted.length);
    // One at a time: the server checks the per-invoice cap per upload, and a
    // sequence keeps each failure attached to the file that caused it.
    for (const file of screened.accepted) {
      try {
        await upload.mutateAsync(file);
        added += 1;
      } catch (err) {
        failures.push(`${file.name}: ${describeError(err)}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    setProblems(failures);
    if (added > 0) {
      await queryClient.invalidateQueries({ queryKey: key });
      toast.success(added === 1 ? 'Attachment added' : `${added} attachments added`);
    }
  };

  // Failures land in the list under the drop zone, in this card — not in a
  // toast somewhere else on the page.
  const download = useMutation({
    mutationFn: (a: InvoiceAttachment) => invoicesApi.downloadAttachment(invoiceId, a.id),
    onMutate: () => setProblems([]),
    onSuccess: ({ blob, filename }, a) => triggerDownload(blob, filename ?? a.filename),
    onError: (err, a) =>
      setProblems([
        `${a.filename}: ${err instanceof ApiError ? err.message : 'could not be downloaded'}`,
      ]),
  });

  const remove = useMutation({
    mutationFn: (a: InvoiceAttachment) => invoicesApi.deleteAttachment(invoiceId, a.id),
    onMutate: () => setProblems([]),
    onSuccess: (_res, a) => {
      toast.success(`Removed ${a.filename}`);
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err, a) =>
      setProblems([
        `${a.filename}: ${err instanceof ApiError ? err.message : 'could not be removed'}`,
      ]),
    onSettled: () => setToRemove(null),
  });

  return (
    <section className="rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-medium text-sm">Attachments</h2>
        {list.isSuccess ? (
          <span className="text-muted-foreground text-xs">
            {attachments.length} of {MAX_INVOICE_ATTACHMENTS}
          </span>
        ) : null}
      </div>

      {list.isPending ? (
        <p className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="size-4 animate-spin" />
          Loading attachments…
        </p>
      ) : list.isError ? (
        // A failed load must not read as "no attachments" (G15).
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 p-2.5">
          <p className="text-destructive text-sm">
            {list.error instanceof ApiError
              ? list.error.message
              : 'Could not load the attachments.'}
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : attachments.length === 0 ? (
        <p className="text-muted-foreground text-sm">No attachments yet.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <Paperclip className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  className="block max-w-full truncate text-left font-medium hover:underline"
                  onClick={() => download.mutate(a)}
                  disabled={download.isPending}
                  title={`Download ${a.filename}`}
                >
                  {a.filename}
                </button>
                <p className="text-muted-foreground text-xs">
                  {formatBytes(a.sizeBytes)} · {dateFormat.format(new Date(a.createdAt))}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {/* Always offered, sent invoices included: the file name is a
                    download link too, but a button is what people look for. */}
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Download ${a.filename}`}
                  title={`Download ${a.filename}`}
                  onClick={() => download.mutate(a)}
                  disabled={download.isPending}
                >
                  <Download className="size-4" />
                </Button>
                {editable ? (
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Remove ${a.filename}`}
                    onClick={() => setToRemove(a)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editable ? (
        <FileDropZone
          className="mt-3 p-5"
          accept={INVOICE_ATTACHMENT_ACCEPT}
          multiple
          disabled={uploading > 0 || attachments.length >= MAX_INVOICE_ATTACHMENTS}
          icon={<Paperclip className="size-6 text-muted-foreground" />}
          label="Drop files here"
          buttonLabel="Choose files"
          hint="PDF, PNG, JPG, XLSX, CSV or DOCX — up to 10 MB each."
          onFiles={(files) => void handleFiles(files)}
        >
          {uploading > 0 ? (
            <p className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="size-4 animate-spin" />
              Uploading {uploading === 1 ? '1 file' : `${uploading} files`}…
            </p>
          ) : null}
        </FileDropZone>
      ) : list.isSuccess && lockedBy !== null ? (
        <p className="mt-2 text-muted-foreground text-xs">
          {lockedBy === 'sent'
            ? 'Attachments are locked once the invoice has been sent.'
            : 'Locked while the invoice is approved. Unlock it to change them.'}
        </p>
      ) : null}

      {problems.length > 0 ? (
        <ul className="mt-2 space-y-0.5 text-destructive text-xs">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={toRemove !== null}
        title={toRemove ? `Remove ${toRemove.filename}?` : 'Remove attachment?'}
        description="It will be removed from this invoice. This cannot be undone."
        confirmLabel="Remove"
        destructive
        confirmDisabled={remove.isPending}
        onConfirm={() => {
          if (toRemove) remove.mutate(toRemove);
        }}
        onCancel={() => setToRemove(null)}
      />
    </section>
  );
}
